import { useState } from 'react';
import { DndContext, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { type CentroEscolha, type LadoCentro, listasDe, moverCentros } from './centros-listas';

const BOTAO = 'flex size-8 items-center justify-center rounded-[9px] border border-input bg-card text-small font-semibold text-ink-body hover:bg-line-soft disabled:opacity-40';
const TITULO: Record<LadoCentro, string> = { naoConsiderados: 'Não considerados', considerados: 'Considerados' };

interface Props {
  /** Todos os centros que podem entrar (analiticos + os ja gravados no critério). */
  opcoes: CentroEscolha[];
  /** Ordem dos considerados (vira centroIds). */
  considerados: string[];
  onChange: (considerados: string[]) => void;
  disabled?: boolean;
}

/**
 * Duas listas de centros analiticos. Move por botao (›, ‹, ››, ‹‹) ou arrastando; a lista "considerados" e ordenada
 * (arrastar sobre um item insere antes dele). Selecao por clique/Espaço no item (botao nativo).
 */
export function SelecaoCentros({ opcoes, considerados, onChange, disabled = false }: Props) {
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const universo = opcoes.map((o) => o.id);
  const rotulo = new Map(opcoes.map((o) => [o.id, o.rotulo]));
  const listas = listasDe(universo, considerados);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const listaDo = (lado: LadoCentro) => listas[lado];
  const marcadosDo = (lado: LadoCentro) => selecionados.filter((id) => listaDo(lado).includes(id));

  function mover(ids: string[], destino: LadoCentro, antesDe?: string) {
    if (ids.length === 0) return;
    onChange(moverCentros(considerados, universo, ids, destino, antesDe));
    setSelecionados((s) => s.filter((id) => !ids.includes(id)));
  }

  function aoSoltar(e: DragEndEvent) {
    const ativo = e.active.data.current as { id: string } | undefined;
    const alvo = e.over?.data.current as { lado: LadoCentro; id?: string } | undefined;
    if (!ativo || !alvo) return;
    if (alvo.id === ativo.id) return;
    mover([ativo.id], alvo.lado, alvo.id);
  }

  const alternar = (id: string) => setSelecionados((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const coluna = (lado: LadoCentro) => (
    <Coluna
      lado={lado}
      titulo={TITULO[lado]}
      ids={listaDo(lado)}
      rotulo={rotulo}
      selecionados={selecionados}
      onAlternar={alternar}
      disabled={disabled}
    />
  );

  return (
    <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={aoSoltar}>
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]" data-testid="selecao-centros">
        {coluna('naoConsiderados')}
        <div className="flex flex-row justify-center gap-2 md:flex-col md:justify-center" role="group" aria-label="Mover centros">
          <button type="button" className={BOTAO} aria-label="Considerar selecionados" title="Considerar selecionados" disabled={disabled || marcadosDo('naoConsiderados').length === 0} onClick={() => mover(marcadosDo('naoConsiderados'), 'considerados')}>
            ›
          </button>
          <button type="button" className={BOTAO} aria-label="Não considerar selecionados" title="Não considerar selecionados" disabled={disabled || marcadosDo('considerados').length === 0} onClick={() => mover(marcadosDo('considerados'), 'naoConsiderados')}>
            ‹
          </button>
          <button type="button" className={BOTAO} aria-label="Considerar todos" title="Considerar todos" disabled={disabled || listas.naoConsiderados.length === 0} onClick={() => mover(listas.naoConsiderados, 'considerados')}>
            ››
          </button>
          <button type="button" className={BOTAO} aria-label="Não considerar nenhum" title="Não considerar nenhum" disabled={disabled || listas.considerados.length === 0} onClick={() => mover(listas.considerados, 'naoConsiderados')}>
            ‹‹
          </button>
        </div>
        {coluna('considerados')}
      </div>
    </DndContext>
  );
}

function Coluna({
  lado,
  titulo,
  ids,
  rotulo,
  selecionados,
  onAlternar,
  disabled,
}: {
  lado: LadoCentro;
  titulo: string;
  ids: string[];
  rotulo: Map<string, string>;
  selecionados: string[];
  onAlternar: (id: string) => void;
  disabled: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `coluna:${lado}`, data: { lado } });
  return (
    <section aria-label={titulo} className="flex min-h-[220px] flex-col rounded-xl border border-border bg-card">
      <h4 className="flex items-center justify-between border-b border-border px-3 py-2 text-caption font-semibold text-ink-secondary">
        {titulo}
        <span className="tabular text-ink-muted">{ids.length}</span>
      </h4>
      <ul
        ref={setNodeRef}
        aria-label={`Centros ${titulo.toLowerCase()}`}
        className={cn('flex flex-1 flex-col gap-1 p-2', isOver && 'bg-line-soft')}
        data-testid={`lista-${lado}`}
      >
        {ids.length === 0 && <li className="px-2 py-6 text-center text-small text-ink-muted">Nenhum centro</li>}
        {ids.map((id) => (
          <ItemCentro
            key={id}
            id={id}
            lado={lado}
            rotulo={rotulo.get(id) ?? id}
            marcado={selecionados.includes(id)}
            onAlternar={onAlternar}
            disabled={disabled}
          />
        ))}
      </ul>
    </section>
  );
}

function ItemCentro({
  id,
  lado,
  rotulo,
  marcado,
  onAlternar,
  disabled,
}: {
  id: string;
  lado: LadoCentro;
  rotulo: string;
  marcado: boolean;
  onAlternar: (id: string) => void;
  disabled: boolean;
}) {
  const arrasto = useDraggable({ id: `centro:${id}`, data: { id, lado }, disabled });
  const soltar = useDroppable({ id: `item:${id}`, data: { lado, id } });
  return (
    <li
      ref={(el) => {
        arrasto.setNodeRef(el);
        soltar.setNodeRef(el);
      }}
      className={cn('rounded-[9px] border', soltar.isOver ? 'border-primary' : 'border-transparent', arrasto.isDragging && 'opacity-40')}
    >
      <button
        type="button"
        {...arrasto.listeners}
        aria-pressed={marcado}
        disabled={disabled}
        onClick={() => onAlternar(id)}
        className={cn(
          'flex w-full cursor-grab items-center gap-2 rounded-[9px] px-2.5 py-1.5 text-left text-small text-ink-body hover:bg-line-soft disabled:cursor-default',
          marcado && 'bg-line-soft font-semibold',
        )}
      >
        <span aria-hidden="true" className="text-ink-disabled">⋮⋮</span>
        <span className="min-w-0 flex-1 truncate">{rotulo}</span>
        <span className="sr-only">{lado === 'considerados' ? '(considerado)' : '(não considerado)'}</span>
      </button>
    </li>
  );
}
